import { useMemo, useState } from 'react';
import { PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from '@/components/ui/combobox';

export type MultiIdComboboxOption = { value: string; label: string };

/**
 * The combobox list is portaled, so picking an option reads as an outside press
 * to an enclosing popover and would close it mid-selection. Pass this to the
 * popover's `onOpenChange` to tell the two apart.
 */
export function isComboboxDismissal(event: Event): boolean {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      '[data-slot="combobox-content"], [data-slot="combobox-list"]'
    )
  );
}

/** Chip-style multi-select over opaque ids, resolving each chip to its label. */
export function MultiIdCombobox({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  creatable = false,
  createLabel = 'Add',
  onCreate,
}: {
  options: MultiIdComboboxOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shows + when the typed text is not already selected. */
  creatable?: boolean;
  createLabel?: string;
  onCreate?: (value: string) => void;
}) {
  const [query, setQuery] = useState('');
  const labelByValue = useMemo(
    () => new Map(options.map((opt) => [opt.value, opt.label])),
    [options]
  );
  const items = useMemo(() => options.map((opt) => opt.value), [options]);
  const trimmed = query.trim();
  const match = options.find(
    (option) =>
      option.value.toLowerCase() === trimmed.toLowerCase() ||
      option.label.toLowerCase() === trimmed.toLowerCase()
  );
  const nextValue = match?.value ?? trimmed;
  const alreadySelected = value.some(
    (selected) => selected.toLowerCase() === nextValue.toLowerCase()
  );
  const showCreate =
    creatable && !disabled && trimmed.length > 0 && !alreadySelected;

  const addTypedValue = () => {
    if (!showCreate) return;
    if (!match) onCreate?.(trimmed);
    onChange([...value, nextValue]);
    setQuery('');
  };

  return (
    <Combobox
      multiple
      items={items}
      value={value}
      disabled={disabled}
      itemToStringLabel={(id: string) => labelByValue.get(id) ?? id}
      {...(creatable
        ? {
            inputValue: query,
            onInputValueChange: (next: string) => setQuery(next),
          }
        : {})}
      onValueChange={(next) => onChange((next as string[]) ?? [])}
    >
      <ComboboxChips className="tw:w-full">
        <ComboboxValue>
          {value.map((id) => (
            <ComboboxChip key={id}>{labelByValue.get(id) ?? id}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={placeholder} disabled={disabled} />
        {showCreate ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={createLabel}
            onMouseDown={(event) => {
              event.preventDefault();
              addTypedValue();
            }}
          >
            <PlusIcon />
          </Button>
        ) : null}
      </ComboboxChips>
      <ComboboxContent className="dark">
        <ComboboxEmpty>No matches</ComboboxEmpty>
        <ComboboxList>
          {(item: string) => (
            <ComboboxItem key={item} value={item}>
              {labelByValue.get(item) ?? item}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
